import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Asset from 'src/app/model/asset';
import { SnackService } from 'src/app/services/snack.service';
import { ClientSignSelectorHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import {
  AtelierControllerService,
  EmployeeRecoveryStatsDTO,
  RecoveryListFilterCommand,
} from '../service/atelier-controller.service';
import { AtelierRecoveryListItem } from './model/atelier-recovery-list-item';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { DEFAULT_DEBOUNCE_TIME } from 'src/globals/globals';
import {
  RecupMethodChoiceArray,
  RecupMethodChoices,
  SignRecupMethod,
} from '../../../model/sign/enums/sign-recup-method';
import { BranchControllerRequestService } from '../../../services/branch/branch-controller-request.service';
import { ProperPageResult } from '../../../model/proper-page-result';
import Branch from '../../../model/branch/branch';
import { AtelierService } from '../../../services/atelier-service';

@Component({
  selector: 'atelier-recovery',
  templateUrl: './atelier-recovery.component.html',
  styleUrls: ['./atelier-recovery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AtelierRecoveryComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  private static readonly LOCAL_STORAGE_COMPANY_SEARCH_TERM_KEY =
    'AtelierRecoveryComponent-companySearchTerm';
  private static readonly LOCAL_STORAGE_PHONE_SEARCH_TERM_KEY =
    'AtelierRecoveryComponent-phoneSearchTerm';
  private static readonly LOCAL_STORAGE_BRANCH_SEARCH_TERM_KEY =
    'AtelierRecoveryComponent-branchSearchTerm';
  private static readonly LOCAL_STORAGE_DATE_SEARCH_KEY =
    'AtelierRecoveryComponent-dateSearch';

  recupMethods: SignRecupMethod[] = RecupMethodChoiceArray;
  protected readonly RecupMethodChoices = RecupMethodChoices;

  recoveryList: AtelierRecoveryListItem[] = [];
  branchList: Branch[];
  rendered: AtelierRecoveryListItem[] = [];
  renderedStream: Subject<AtelierRecoveryListItem[]> = new Subject<
    AtelierRecoveryListItem[]
  >();
  performanceList: EmployeeRecoveryStatsDTO[] = [];

  chunkSize = 4;
  sortByCompany: boolean = false;
  loading: boolean = true;
  rendering = false;

  getRecoveryListSubject = new Subject<void>();
  getBranchesListSubject = new Subject<void>();

  companySearchTerm: string;
  private searchCompany = new Subject<never>();
  phoneSearchTerm: string;
  private searchPhone = new Subject<never>();
  branchSearchTerm: string;
  private searchBranch = new Subject<never>();
  dateSearch: Date;
  private searchDate = new Subject<never>();

  constructor(
    private atelierService: AtelierService,
    private atelierControllerService: AtelierControllerService,
    private branchControllerRequestService: BranchControllerRequestService,
    private snackService: SnackService,
    private _lightbox: Lightbox,
    private cd: ChangeDetectorRef,
    private router: Router,
    private clientCompanySelectService: ClientCompanySelectService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initialiseSearchTerms();
    this.addBranchListSubscription();
    this.addPerformanceStatsSubscription();
    this.addRecoveryListSubscription();
    this.addSearchSubscriptions();
  }

  private addPerformanceStatsSubscription() {
    const observable = this.atelierService
      .getSelectedAtelierDateSubscribable()
      .pipe(
        startWith(
          this.atelierService.getSelectedAtelierDateSubscribable().getValue()
        ),
        switchMap(async (date: Date) => {
          this.loading = true;
          try {
            return await this.atelierControllerService.getRecoveryPerformanceStats(
              date
            );
          } catch (error) {
            console.error('Error fetching recovery performance stats:', error);
            return [];
          }
        }),
        catchError(() => [])
      );

    this.addSubscription(
      observable,
      (recoveryList: EmployeeRecoveryStatsDTO[]) => {
        this.performanceList = recoveryList;
        this.loading = false;
        this.startRendering();
      },
      (error) => {
        console.error('Subscription error:', error);
        this.loading = false;
      }
    );
  }

  private addRecoveryListSubscription() {
    this.addSubscription(
      this.getRecoveryListSubject.pipe(
        startWith([]),
        switchMap(async () => {
          this.loading = true;
          const filterCommand = this.buildFilterCommand();
          return await this.atelierControllerService.recoveryList(
            filterCommand
          );
        }),
        catchError(() => []),
        takeUntil(this.destroyThisComponent)
      ),
      (recoveryList: AtelierRecoveryListItem[]) => {
        this.processItems(recoveryList);
        this.recoveryList = recoveryList;
        this.loading = false;
        this.startRendering();
      }
    );
  }

  private addSearchSubscriptions() {
    this.addSubscription(
      this.searchCompany,
      () => {
        this.loading = true;
        this.cd.markForCheck();
        this.getRecoveryListSubject.next();
        localStorage.setItem(
          AtelierRecoveryComponent.LOCAL_STORAGE_COMPANY_SEARCH_TERM_KEY,
          this.companySearchTerm
        );
      },
      GeneralHelper.noop,
      DEFAULT_DEBOUNCE_TIME
    );

    this.addSubscription(
      this.searchPhone,
      () => {
        this.loading = true;
        this.cd.markForCheck();
        this.getRecoveryListSubject.next();
        localStorage.setItem(
          AtelierRecoveryComponent.LOCAL_STORAGE_PHONE_SEARCH_TERM_KEY,
          this.phoneSearchTerm
        );
      },
      GeneralHelper.noop,
      DEFAULT_DEBOUNCE_TIME
    );

    this.addSubscription(
      this.searchBranch,
      () => {
        this.loading = true;
        this.cd.markForCheck();
        this.getRecoveryListSubject.next();
        localStorage.setItem(
          AtelierRecoveryComponent.LOCAL_STORAGE_BRANCH_SEARCH_TERM_KEY,
          this.branchSearchTerm
        );
      },
      GeneralHelper.noop,
      DEFAULT_DEBOUNCE_TIME
    );

    this.addDateSearchSubscription();
  }

  private addBranchListSubscription() {
    this.addSubscription(
      this.getBranchesListSubject.pipe(
        startWith([]),
        switchMap(async () => {
          this.loading = true;
          return this.branchControllerRequestService.getPaged();
        }),
        catchError(() => []),
        takeUntil(this.destroyThisComponent)
      ),
      (pageResult: ProperPageResult<Branch>) => {
        this.branchList = pageResult.items;
        this.loading = false;
        this.startRendering();
      }
    );
  }

  private addDateSearchSubscription(): void {
    this.addSubscription(
      this.searchDate,
      () => {
        this.loading = true;
        this.cd.markForCheck();
        this.getRecoveryListSubject.next();
        localStorage.setItem(
          AtelierRecoveryComponent.LOCAL_STORAGE_DATE_SEARCH_KEY,
          this.dateSearch ? this.dateSearch.toISOString() : ''
        );
      },
      GeneralHelper.noop,
      DEFAULT_DEBOUNCE_TIME
    );
  }

  private initialiseSearchTerms(): void {
    const companySearchTerm = localStorage.getItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_COMPANY_SEARCH_TERM_KEY
    );
    this.companySearchTerm = companySearchTerm || null;

    const phoneSearchTerm = localStorage.getItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_PHONE_SEARCH_TERM_KEY
    );
    this.phoneSearchTerm = phoneSearchTerm || null;

    const branchSearchTerm = localStorage.getItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_BRANCH_SEARCH_TERM_KEY
    );
    this.branchSearchTerm = branchSearchTerm || null;

    const dateSearch = localStorage.getItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_DATE_SEARCH_KEY
    );
    this.dateSearch = dateSearch ? new Date(dateSearch) : null;
  }

  private buildFilterCommand(): RecoveryListFilterCommand {
    return {
      sortByCompany: this.sortByCompany,
      companySearchTerm: !StringHelper.isEmptyOrWhiteSpace(
        this.companySearchTerm
      )
        ? this.companySearchTerm
        : undefined,
      phoneSearchTerm: !StringHelper.isEmptyOrWhiteSpace(this.phoneSearchTerm)
        ? this.phoneSearchTerm
        : undefined,
      branchSearchTerm: !StringHelper.isEmptyOrWhiteSpace(this.branchSearchTerm)
        ? this.branchSearchTerm
        : undefined,
      dateSearch: this.dateSearch || undefined,
    };
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getRecoveryListSubject != null) {
      this.getRecoveryListSubject.complete();
      this.getRecoveryListSubject = null;
    }
    if (this.searchCompany != null) {
      this.searchCompany.complete();
      this.searchCompany = null;
    }
    if (this.searchPhone != null) {
      this.searchPhone.complete();
      this.searchPhone = null;
    }
    if (this.searchBranch != null) {
      this.searchBranch.complete();
      this.searchBranch = null;
    }
    if (this.searchDate != null) {
      this.searchDate.complete();
      this.searchDate = null;
    }
    if (this.renderedStream != null) {
      this.renderedStream.complete();
      this.renderedStream = null;
    }
  }

  ngAfterContentChecked(): void {
    if (this.rendering) {
      this.renderNextChunk();
    }
  }

  private processItems(recoveryItems: AtelierRecoveryListItem[]): void {
    if (CollectionHelper.isPopulated(recoveryItems)) {
      recoveryItems.forEach((i) => {
        if (i.example != null) {
          i.exampleStyle = ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${i.example.uuid}`
          );
        }
        i.inputRecoveryAmount = i.suggestedRecoveryAmount;
      });
    } else {
      this.snackService.showWarning('Geen recupborden gevonden');
    }
  }

  private startRendering(): void {
    if (!this.rendering) {
      this.rendered.length = 0;
      this.rendering = true;
      this.cd.markForCheck();
    }
  }

  private renderNextChunk(): void {
    if (this.rendered.length >= this.recoveryList.length) {
      if (this.recoveryList.length === 0) {
        this.rendered.length = 0;
      }
      this.renderedStream.next(this.rendered);
      this.rendering = false;
      return;
    }

    this.rendered = [
      ...this.rendered,
      ...this.recoveryList.slice(
        this.rendered.length,
        Math.min(
          this.recoveryList.length,
          this.rendered.length + this.chunkSize
        )
      ),
    ];
    this.renderedStream.next(this.rendered);
    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  sortByCompanyChanged(): void {
    this.getRecoveryListSubject.next();
  }

  doSearchCompany(): void {
    this.searchCompany.next();
  }

  doSearchPhone(): void {
    this.searchPhone.next();
  }

  doSearchBranch(): void {
    this.searchBranch.next();
  }

  doSearchDate(): void {
    this.searchDate.next();
  }

  decrementRecoveryAmount(item: AtelierRecoveryListItem): void {
    item.inputRecoveryAmount = Math.max(0, item.inputRecoveryAmount - 1);
  }

  incrementRecoveryAmount(item: AtelierRecoveryListItem): void {
    item.inputRecoveryAmount = Math.min(
      item.suggestedRecoveryAmount,
      item.inputRecoveryAmount + 1
    );
  }

  inputRecoveryAmountChanged(item: AtelierRecoveryListItem): void {
    const validInputRecoveryAmount = Math.min(
      item.inputRecoveryAmount,
      item.suggestedRecoveryAmount
    );
    item.inputRecoveryAmount = <any>Number(validInputRecoveryAmount);
  }

  openLightbox(example: Asset): void {
    const album: IAlbum[] = [
      {
        src: `/api/assets/${example.uuid}`,
        thumb: `/api/assets/${example.uuid}`,
      },
    ];
    this._lightbox.open(album);
  }

  handleBoardRecovery(item: AtelierRecoveryListItem): void {
    item.recoveryInProgress = true;
    this.cd.markForCheck();
    this.atelierControllerService
      .recoverBoard(
        item.repoId,
        item.boardPartId,
        item.inputRecoveryAmount, //if destroy set to 0 in the backend
        item.recupMethod ? item.recupMethod : RecupMethodChoices.DESTROY //for custom boards
      )
      .then(() => {
        const isRecupMethodDestroy =
          item.recupMethod?.value === RecupMethodChoices.DESTROY.value || true; //default to true for custom boards
        const hasPositiveRecoveryAmount = item.inputRecoveryAmount > 0;

        this.snackService.showSuccess(
          !isRecupMethodDestroy && hasPositiveRecoveryAmount
            ? 'Bord succesvol gerecupereerd'
            : 'Bord succesvol als niet gerecupereerd gemarkeerd'
        );

        this.removeItem(item);
      })
      .finally(() => {
        item.recoveryInProgress = false;
        this.cd.markForCheck();
      });
  }

  private removeItem(item: AtelierRecoveryListItem): void {
    CollectionHelper.filterInPlace(this.rendered, (e) => e !== item);
  }

  trackByProjectId(index: number, item: AtelierRecoveryListItem): number {
    return item.projectId;
  }

  search(): void {
    this.getRecoveryListSubject.next();
  }

  resetPhoneSearch(): void {
    this.phoneSearchTerm = null;
    localStorage.removeItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_PHONE_SEARCH_TERM_KEY
    );
    this.getRecoveryListSubject.next();
  }

  resetCompanySearch(): void {
    this.companySearchTerm = null;
    localStorage.removeItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_COMPANY_SEARCH_TERM_KEY
    );
    this.getRecoveryListSubject.next();
  }

  resetBranchSearch(): void {
    this.branchSearchTerm = null;
    localStorage.removeItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_BRANCH_SEARCH_TERM_KEY
    );
    this.getRecoveryListSubject.next();
  }

  resetDateSearch(): void {
    this.dateSearch = null;
    localStorage.removeItem(
      AtelierRecoveryComponent.LOCAL_STORAGE_DATE_SEARCH_KEY
    );
    this.getRecoveryListSubject.next();
  }

  openRecupDetail(item: AtelierRecoveryListItem): void {
    this.clientCompanySelectService.selectCompany(item.company);

    this.router.navigateByUrl(
      `/atelier-recup-detail/${item.projectId}/${item.repoId}`
    );
  }

  onRecupMethodChange(
    item: AtelierRecoveryListItem,
    selectedMethod: SignRecupMethod,
    event: MouseEvent
  ): void {
    event.preventDefault();
    if (item.recupMethod !== selectedMethod) {
      item.recupMethod = selectedMethod;
      this.cd.markForCheck();
    }
  }
}
